/**
 * Diccionario local de correcciones ortográficas aprendidas.
 * - replacements: palabra/frase → corrección aceptada (se aplica sola la próxima vez)
 * - ignored: términos a no sugerir (p. ej. razones sociales / RFC)
 */

export const SPELLCHECK_LEARNED_STORAGE_KEY = "vf:spellcheck_learned_v1";

const MAX_ENTRIES = 400;

export function normalizeSpellKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFC");
}

export function emptySpellcheckLearned() {
  return { replacements: {}, ignored: {} };
}

export function loadSpellcheckLearned() {
  try {
    const raw = localStorage.getItem(SPELLCHECK_LEARNED_STORAGE_KEY);
    if (!raw) return emptySpellcheckLearned();
    const parsed = JSON.parse(raw);
    const replacements = {};
    const ignored = {};
    if (parsed && typeof parsed.replacements === "object" && parsed.replacements) {
      for (const [k, v] of Object.entries(parsed.replacements)) {
        const key = normalizeSpellKey(k);
        const val = String(v || "").trim();
        if (key && val) replacements[key] = val;
      }
    }
    if (parsed && typeof parsed.ignored === "object" && parsed.ignored) {
      for (const [k, v] of Object.entries(parsed.ignored)) {
        const key = normalizeSpellKey(k);
        if (key && v) ignored[key] = true;
      }
    }
    return { replacements, ignored };
  } catch {
    return emptySpellcheckLearned();
  }
}

function pruneMap(map, max) {
  const keys = Object.keys(map);
  if (keys.length <= max) return map;
  const keep = keys.slice(keys.length - max);
  const out = {};
  keep.forEach((k) => {
    out[k] = map[k];
  });
  return out;
}

export function saveSpellcheckLearned(state) {
  const next = {
    replacements: pruneMap({ ...(state?.replacements || {}) }, MAX_ENTRIES),
    ignored: pruneMap({ ...(state?.ignored || {}) }, MAX_ENTRIES),
  };
  try {
    localStorage.setItem(SPELLCHECK_LEARNED_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota / private mode */
  }
  return next;
}

export function rememberSpellReplacement(word, replacement) {
  const key = normalizeSpellKey(word);
  const val = String(replacement || "").trim();
  if (!key || !val) return loadSpellcheckLearned();
  const cur = loadSpellcheckLearned();
  const ignored = { ...cur.ignored };
  delete ignored[key];
  return saveSpellcheckLearned({
    replacements: { ...cur.replacements, [key]: val },
    ignored,
  });
}

export function rememberSpellIgnored(word) {
  const key = normalizeSpellKey(word);
  if (!key) return loadSpellcheckLearned();
  const cur = loadSpellcheckLearned();
  const replacements = { ...cur.replacements };
  delete replacements[key];
  return saveSpellcheckLearned({
    replacements,
    ignored: { ...cur.ignored, [key]: true },
  });
}

export function matchWordFromText(text, match) {
  const offset = Number(match?.offset) || 0;
  const length = Number(match?.length) || 0;
  if (match?.word) return String(match.word);
  if (length > 0) return String(text || "").slice(offset, offset + length);
  return "";
}

/**
 * Filtra ignorados y separa matches con corrección aprendida.
 */
export function partitionSpellMatches(text, matches, learned) {
  const replacements = learned?.replacements || {};
  const ignored = learned?.ignored || {};
  const toApply = [];
  const remaining = [];
  for (const match of matches || []) {
    const word = matchWordFromText(text, match);
    const key = normalizeSpellKey(word);
    if (!key || ignored[key]) continue;
    const learnedRepl = replacements[key];
    if (learnedRepl) {
      toApply.push({
        ...match,
        word,
        replacements: [learnedRepl, ...(match.replacements || []).filter((r) => r !== learnedRepl)],
      });
    } else {
      remaining.push(match);
    }
  }
  return { toApply, remaining };
}
